import type { TeamMember } from "../db/schema";
import { useOptionalUser } from "./user";

// Global roles
export const GLOBAL_ROLE_HIERARCHY = {
  admin: 2,
  user: 1,
} as const;

export type GlobalRole = keyof typeof GLOBAL_ROLE_HIERARCHY;

export const DEFAULT_GLOBAL_ROLE = "user";

export enum GlobalRoles {
  ADMIN = "admin",
  USER = "user",
}

export const availableGlobalRoles = Object.keys(
  GLOBAL_ROLE_HIERARCHY
) as GlobalRole[];

// Team roles
export const TEAM_ROLE_HIERARCHY = {
  owner: 7,
  admin: 6,
  manager: 5,
  monitor: 4,
  contributor: 3,
  operator: 2,
  viewer: 1,
} as const;

export type TeamRole = keyof typeof TEAM_ROLE_HIERARCHY;

export enum TeamRoles {
  OWNER = "owner",
  ADMIN = "admin",
  MANAGER = "manager",
  MONITOR = "monitor",
  CONTRIBUTOR = "contributor",
  OPERATOR = "operator",
  VIEWER = "viewer",
}

export const availableTeamRoles = Object.keys(
  TEAM_ROLE_HIERARCHY
) as TeamRole[];

export const teamRoleToName = {
  owner: "dueño",
  admin: "administrador",
  manager: "jefe de terminal",
  monitor: "monitor",
  contributor: "colaborador",
  operator: "operador terminal",
  viewer: "usuario",
} as const;

export const DEFAULT_TEAM_ROLE = "viewer";

export function useRoles() {
  const maybeUser = useOptionalUser();
  if (!maybeUser) {
    throw new Error(
      "No user found in root loader, but user is required by useRoles. If user is optional, try useOptionalUser instead."
    );
  }

  const globalRole = maybeUser.role?.name;
  const teamRole = maybeUser.teamMember?.teamRole?.name || TeamRoles.VIEWER;

  return {
    globalRole,
    teamRole,
  };
}

export function getIsAdmin({
  teamMember,
  globalRole,
  teamRole,
}: {
  teamMember?: TeamMember;
  globalRole?: GlobalRole;
  teamRole?: TeamRole;
}) {
  let isGlobalAdmin = false;
  let isTeamAdmin = false;

  const teamAdminRoles = ["owner", "admin"] satisfies TeamRole[];

  if (globalRole) {
    isGlobalAdmin = globalRole === "admin";
  }

  if (teamRole) {
    isTeamAdmin = teamAdminRoles.some((role) => teamRole === role);
  }

  if (teamMember) {
    isTeamAdmin = teamAdminRoles.some(
      (role) => teamMember.teamRole.name === role
    );
  }

  return isGlobalAdmin || isTeamAdmin;
}

export function getIsOwner({
  teamMember,
  globalRole,
  teamRole,
}: {
  teamMember?: TeamMember;
  globalRole?: GlobalRole;
  teamRole?: TeamRole;
}) {
  let isGlobalAdmin = false;
  let isTeamOwner = false;

  if (globalRole) {
    isGlobalAdmin = globalRole === "admin";
  }

  if (teamRole) {
    isTeamOwner = teamRole === "owner";
  }

  if (teamMember) {
    isTeamOwner = teamMember.teamRole.name === "owner";
  }

  return isGlobalAdmin || isTeamOwner;
}
